import React, { useState, useEffect, useContext } from 'react'
import styles from './WizardStepBox.less'
import { Grid } from './Grid'
import { FAIcon } from './FAIcon'
import { Button } from './Button'
import { GridCell } from './GridCell'
// import { ShowConfirm } from 'common-data/Actions/ShowNotificationActions'
import { motion, AnimatePresence } from 'framer-motion'
import clsx from 'clsx'
import _ from 'common-tools/LodashHelper'

const StepVariants = {
  slideIn: (direction) => ({
    x: direction > 0 ? 1000 : -1000
  }),
  slideShow: {
    x: 0
  },
  slideOut: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    transition: {
      duration: 0.2
    }
  })
}

const WizardStepContext = React.createContext({})

export function WizardStep (props) {
  const { children, id, title, hideCancelButton } = props

  const stepContext = useContext(WizardStepContext)

  const { onNext, onPrevious, onDone, onCancel, stepNumber, totalSteps,
    tiny = false, small = false, doneButtonText, loading } = stepContext

  const [allowNext, setAllowNext] = useState(false)
  const [allowPrevious, setAllowPrevious] = useState(false)
  const [allowDone, setAllowDone] = useState(false)

  useEffect(() => {
    if (totalSteps === 0) {
      setAllowNext(false)
      setAllowPrevious(false)
      setAllowDone(false)
    } else if (stepNumber === 1 && totalSteps > 0) {
      setAllowNext(true)
      setAllowPrevious(false)
      setAllowDone(false)
    } else if (stepNumber === 1 && totalSteps === 1) {
      setAllowNext(false)
      setAllowPrevious(false)
      setAllowDone(false)
    } else if (stepNumber < totalSteps) {
      setAllowNext(true)
      setAllowPrevious(true)
      setAllowDone(false)
    } else if (stepNumber === totalSteps) {
      setAllowNext(false)
      setAllowPrevious(true)
      setAllowDone(true)
    } else {
      setAllowNext(false)
      setAllowPrevious(true)
      setAllowDone(false)
    }
  }, [stepNumber, totalSteps])

  return <div key={id} className={clsx('wizard', tiny === true ? 'tiny' : small === true ? 'small' : 'large', 'reveal', 'reveal-box')} tabIndex="-1">
    <div className="wizard-title-box">
      <Grid>
        <GridCell small={4} className="wizard-title">{title}</GridCell>
        <GridCell hide={stepNumber === 0} small={4} className="text-center">Step {stepNumber} of {totalSteps}</GridCell>
        <GridCell hide={(stepNumber === 0) || (hideCancelButton)} small={4} className="text-right"><FAIcon toolTip='Cancel' onClick={onCancel} className='hand' name='times' /></GridCell>
      </Grid>
    </div>
    <div className="wizard-body">
      {children}
    </div>
    <div className="wizard-foot">
      <Grid hide={stepNumber === 0}>
        <GridCell small={6}><Button loading={loading} disabled={!allowPrevious} className="logout-button" onClick={onPrevious}>prev</Button></GridCell>
        <GridCell hide={allowDone} className='text-right' small={6}><Button loading={loading} disabled={!allowNext} className="a-switch-button" onClick={onNext}>next</Button></GridCell>
        <GridCell hide={!allowDone} className='text-right' small={6}><Button loading={loading} className="a-switch-button" onClick={onDone}>{doneButtonText || 'Done'}</Button></GridCell>
      </Grid>
    </div>
  </div>
}

export function WizardStepContainer (props) {
  const { children = [], tiny = false, small = false, beforeStepAction, afterStepAction,
    onDone, doneButtonText, loading, onCancel } = props

  const [activeStep, setActiveStep] = useState()
  const [[stepIndex, stepDirection], setStep] = useState([0, 1])
  const [stepMap, setSetMap] = useState([])
  const [cancelReturnStepIndex, setCancelReturnStepIndex] = useState(0)

  async function handleOnNext () {
    if (_.isFunction(beforeStepAction)) {
      if (await beforeStepAction(stepMap[stepIndex + 1], stepMap[stepIndex], 'next')) {
        return // abort step
      }
    }
    setStep([stepIndex + 1, 1])
    if (_.isFunction(afterStepAction)) {
      await afterStepAction(stepMap[stepIndex + 1], stepMap[stepIndex], 'next')
    }
  }

  async function handleOnPrevious () {
    if (_.isFunction(beforeStepAction)) {
      if (await beforeStepAction(stepMap[stepIndex - 1], stepMap[stepIndex], 'previous')) {
        return // abort step
      }
    }
    setStep([stepIndex - 1, -1])
    if (_.isFunction(afterStepAction)) {
      await afterStepAction(stepMap[stepIndex - 1], stepMap[stepIndex], 'previous')
    }
  }

  function handleOnCancel (e) {
    // e.preventDefault()
    // e.stopPropagation()
    // e.nativeEvent.stopImmediatePropagation()
    if (_.isFunction(onCancel)) {
      onCancel()
    }
  }

  function handleOnContinue (e) {
    // e.preventDefault()
    // e.stopPropagation()
    // e.nativeEvent.stopImmediatePropagation()
    setStep([cancelReturnStepIndex, 1])
  }

  function handleRequestCancel () {
    setCancelReturnStepIndex(stepIndex)
    setStep([-1, 0])
  }

  useEffect(() => {
    if (_.isArray(children)) {
      setSetMap(_.map(children, (c, i) => (c?.props?.id || `none-${i}`)))
    } else {
      setSetMap([
        children?.props?.id || 'none'
      ])
    }
  }, [children])

  useEffect(() => {
    if (stepIndex === -1) {
      setActiveStep(<WizardStep title='Confirm Cancel'>
        <Grid paddingX>
          <GridCell small={12} className='text-center'><h5>Are you sure you want to quit?</h5></GridCell>
          <GridCell small={12} className='text-center'><p>All your progress will be lost if you continue</p></GridCell>
          <GridCell small={6} className='text-right'><Button primary onClick={handleOnCancel} >Yes</Button></GridCell>
          <GridCell small={6} className='text-left'><Button secondary outline onClick={handleOnContinue}>No</Button></GridCell>
        </Grid>
      </WizardStep>)
    } else {
      setActiveStep(children[stepIndex])
    }
  }, [stepIndex, children])

  return <div className={styles.container}>
    <WizardStepContext.Provider value={{
      onNext: handleOnNext,
      onPrevious: handleOnPrevious,
      onCancel: handleRequestCancel,
      stepNumber: stepIndex + 1,
      totalSteps: children.length,
      small: small,
      tiny: tiny,
      doneButtonText: doneButtonText,
      onDone: onDone,
      loading: loading
    }}>
      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={activeStep?.props?.id}
          custom={stepDirection}
          initial='slideIn'
          animate='slideShow'
          exit='slideOut'
          variants={StepVariants}
          transition={{
            duration: 0.200
          }}
        >
          {activeStep}
        </motion.div>
      </AnimatePresence>
    </WizardStepContext.Provider>
  </div>
}
